import React, { useState, useEffect } from 'react';
import { Link, useInRouterContext, useLocation } from 'react-router-dom';
import api from '../config/api';
import { buildAssetUrl } from '../config/api';
import './WelcomePopup.css';

// Hook to detect if screen is mobile — throttled to avoid excessive re-renders
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    let timeout;
    const handleResize = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => setIsMobile(window.innerWidth < 768), 200);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeout);
    };
  }, []);

  return isMobile;
};

// The storefront welcome popup must never render over the back office: neither on
// /admin/* paths (localhost / main domain) nor on the admin.* subdomain. The guard
// lives here so it holds wherever the component is mounted.
const isAdminLocation = (pathname) => {
  if (typeof window !== 'undefined' && /^admin\./i.test(window.location.hostname)) return true;
  return /^\/admin(\/|$)/i.test(pathname || '');
};

const WelcomePopupBody = ({ pathname }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [popupData, setPopupData] = useState(null);
  const [isClosing, setIsClosing] = useState(false);
  const isMobile = useIsMobile();
  const onAdmin = isAdminLocation(pathname);

  useEffect(() => {
    if (onAdmin) return;
    checkAndFetchPopup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onAdmin]);

  const checkAndFetchPopup = () => {
    const displayInterval = parseInt(localStorage.getItem('popupDisplayInterval')) || 0;
    const lastShownTimestamp = localStorage.getItem('popupLastShown');
    
    let shouldShow = false;
    
    if (displayInterval === 0) {
      // Once per session - check sessionStorage
      const hasSeenPopup = sessionStorage.getItem('hasSeenPopup');
      shouldShow = !hasSeenPopup;
    } else {
      // Time interval - check localStorage with timestamp
      if (!lastShownTimestamp) {
        shouldShow = true;
      } else {
        const lastShown = parseInt(lastShownTimestamp);
        const now = Date.now();
        const intervalMs = displayInterval * 60 * 1000; // Convert minutes to milliseconds
        shouldShow = (now - lastShown) >= intervalMs;
      }
    }
    
    if (shouldShow) {
      fetchPopupSettings();
    }
  };

  const fetchPopupSettings = async () => {
    try {
      const response = await api.get('/popup');
      const data = response.data;
      
      if (data && data.is_active && (data.desktop_image_url || data.mobile_image_url || data.image_url)) {
        // Store display interval setting
        const displayInterval = data.display_interval_minutes !== undefined ? data.display_interval_minutes : 0;
        localStorage.setItem('popupDisplayInterval', displayInterval.toString());
        
        setPopupData(data);
        // Small delay before showing for better UX
        setTimeout(() => {
          setIsVisible(true);
        }, 1000);
      }
    } catch (error) {
      console.error('Error fetching popup settings:', error);
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      
      // Update popup tracking based on display interval
      const displayInterval = popupData?.display_interval_minutes !== undefined 
        ? popupData.display_interval_minutes 
        : parseInt(localStorage.getItem('popupDisplayInterval')) || 0;
      
      if (displayInterval === 0) {
        // Once per session - use sessionStorage
        sessionStorage.setItem('hasSeenPopup', 'true');
      } else {
        // Time interval - store timestamp in localStorage
        localStorage.setItem('popupLastShown', Date.now().toString());
      }
    }, 300);
  };

  const handleOverlayClick = (e) => {
    if (e.target.classList.contains('popup-overlay')) {
      handleClose();
    }
  };

  const handleButtonClick = () => {
    // Update popup tracking based on display interval
    const displayInterval = popupData?.display_interval_minutes !== undefined 
      ? popupData.display_interval_minutes 
      : parseInt(localStorage.getItem('popupDisplayInterval')) || 0;
    
    if (displayInterval === 0) {
      // Once per session - use sessionStorage
      sessionStorage.setItem('hasSeenPopup', 'true');
    } else {
      // Time interval - store timestamp in localStorage
      localStorage.setItem('popupLastShown', Date.now().toString());
    }
  };

  if (onAdmin || !isVisible || !popupData) {
    return null;
  }

  const isExternalLink = popupData.button_link && popupData.button_link.startsWith('http');

  return (
    <div 
      className={`popup-overlay ${isClosing ? 'popup-closing' : ''}`}
      onClick={handleOverlayClick}
    >
      <div className={`popup-container ${isClosing ? 'popup-container-closing' : ''}`}>
        {/* Close Button */}
        <button className="popup-close" onClick={handleClose} aria-label="Close popup">
          <i className="la la-times"></i>
        </button>

        {/* Popup Image - Use mobile_image_url on mobile, desktop_image_url on desktop, fallback to image_url */}
        <div className="popup-image-wrapper">
          <img 
            src={buildAssetUrl(
              isMobile 
                ? (popupData.mobile_image_url || popupData.desktop_image_url || popupData.image_url)
                : (popupData.desktop_image_url || popupData.image_url)
            )} 
            alt={popupData.title || 'Advertisement'}
            className="popup-image"
          />
        </div>

        {/* Popup Content (if title or description exists) */}
        {(popupData.title || popupData.description || popupData.button_text) && (
          <div className="popup-content">
            {popupData.title && (
              <h2 className="popup-title">{popupData.title}</h2>
            )}
            
            {popupData.description && (
              <p className="popup-description">{popupData.description}</p>
            )}
            
            {popupData.button_text && popupData.button_link && (
              isExternalLink ? (
                <a 
                  href={popupData.button_link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="popup-button"
                  onClick={handleButtonClick}
                >
                  {popupData.button_text}
                </a>
              ) : (
                <Link 
                  to={popupData.button_link} 
                  className="popup-button"
                  onClick={handleButtonClick}
                >
                  {popupData.button_text}
                </Link>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Reads the live pathname from the router when mounted inside one, otherwise from
// window.location, so the admin guard works in both cases.
const RoutedWelcomePopup = () => {
  const location = useLocation();
  return <WelcomePopupBody pathname={location.pathname} />;
};

const WelcomePopup = () => {
  const inRouter = useInRouterContext();
  if (inRouter) return <RoutedWelcomePopup />;
  return <WelcomePopupBody pathname={typeof window !== 'undefined' ? window.location.pathname : ''} />;
};

export default WelcomePopup;
